import { useEffect, useRef, useState } from 'react';
import { OUR_HOME_LOCATIONS } from '../data/ourHomesLocations';
import {
  createDevelopmentMapPinIcon,
  loadLeaflet,
  type LeafletLib,
  type LeafletMap,
  type LeafletMarker,
} from '../utils/loadLeaflet';

type OurHomesMapProps = {
  selectedId: string | null;
};

function OurHomesMap({ selectedId }: OurHomesMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markersRef = useRef<Record<string, LeafletMarker>>({});
  const leafletRef = useRef<LeafletLib | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let cancelled = false;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current) return;

        leafletRef.current = L;
        const map = L.map(containerRef.current, { scrollWheelZoom: true });
        mapRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);

        const bounds = L.latLngBounds(
          OUR_HOME_LOCATIONS.map((home) => [home.lat, home.lng] as [number, number])
        );

        const markers: Record<string, LeafletMarker> = {};
        OUR_HOME_LOCATIONS.forEach((home) => {
          const marker = L.marker([home.lat, home.lng], {
            icon: createDevelopmentMapPinIcon(L, false),
          }).addTo(map);

          marker.bindPopup(
            `<div class="our-homes-map-popup"><strong>${home.name}</strong><br /><span>${home.address}</span></div>`
          );

          markers[home.id] = marker;
        });

        markersRef.current = markers;
        map.fitBounds(bounds, { padding: [48, 48], maxZoom: 10 });
        setMapReady(true);
      })
      .catch(() => {
        if (containerRef.current) {
          containerRef.current.innerHTML =
            '<p class="our-homes-map-fallback">Map could not be loaded. Please try again later.</p>';
        }
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markersRef.current = {};
      setMapReady(false);
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const L = leafletRef.current;
    if (!mapReady || !map || !L || !selectedId) return;

    const location = OUR_HOME_LOCATIONS.find((entry) => entry.id === selectedId);
    if (!location) return;

    Object.entries(markersRef.current).forEach(([id, marker]) => {
      const isActive = id === selectedId;
      marker.setIcon(createDevelopmentMapPinIcon(L, isActive));
      if (isActive) {
        marker.openPopup();
      }
    });

    map.panTo([location.lat, location.lng], { animate: true, duration: 0.85 });
  }, [selectedId, mapReady]);

  return (
    <section id="our-homes-map" className="our-homes-map-section" aria-label="Map of Graham Care homes">
      <h2>Find our homes</h2>
      <p className="our-homes-map-intro">
        Click &ldquo;Show on Map&rdquo; on any home to highlight its location.
      </p>
      <div ref={containerRef} className="our-homes-map__container" role="application" />
    </section>
  );
}

export default OurHomesMap;
